import type { ComponentDefinition, Terminal } from "../types";
import type { SimulateStateOption } from "../simulate/states";

export const RV_PANEL_ID = "dist-rv-split-phase-panel";
export const PARALLAX_5355_AC_ID = "dist-parallax-5355-ac-section-6x20";
export const RV_DC_BOARD_ID = "dist-rv-dc-fuse-board-15";
export const RV_MAX_BRANCHES = 6;
export const RV_LEGS = ["l1", "l2"] as const;
export type RvLeg = typeof RV_LEGS[number];
type Link = readonly [string, string];

const terminal = (id: string, label: string, maxAmp: number, direction: Terminal["direction"], side: Terminal["side"], offset: number): Terminal => ({
  id, label, maxAmp, direction, side, offset,
  polarity: id.endsWith("-pe") ? "ground" : "neutral",
});
export const rvBranchPrefix = (leg: RvLeg, branch: number) => `ac-${leg}-branch${branch}`;
export const rvBranchBit = (leg: RvLeg, branch: number) => 1 << (branch + (leg === "l2" ? RV_MAX_BRANCHES : 0));

function allOnMask(count: number): number {
  return RV_LEGS.reduce((mask, leg) => Array.from({ length: count }, (_, i) => i + 1)
    .reduce((m, branch) => m | rvBranchBit(leg, branch), mask), 1);
}

/** Bit 0 is the common two-pole main; each leg has six stable branch bits.
 * Invalid state strings open every hot contact, never infer a copper bus. */
export function rvPanelMask(count: number, state?: string): number {
  if (state == null || state === "all-on") return allOnMask(count);
  if (state === "main-off") return allOnMask(count) & ~1;
  if (state === "branches-off") return 1;
  if (!/^rv:\d{1,4}$/.test(state)) return 0;
  const mask = Number(state.slice(3));
  return Number.isInteger(mask) && mask <= 8191 ? mask & allOnMask(count) : 0;
}

function contactLinks(count: number, mask: number): Link[] {
  const links: Link[] = [["ac-in-n", "ac-bus-n"], ["ac-in-pe", "ac-bus-pe"]];
  for (const leg of RV_LEGS) {
    // Both hot legs open together. Neutral and PE are distinct, unswitched
    // buses. Bus terminals represent the real rails AFTER the main breaker.
    if (mask & 1) links.push([`ac-in-${leg}`, `ac-bus-${leg}`]);
    for (let branch = 1; branch <= count; branch++) {
      const prefix = rvBranchPrefix(leg, branch);
      if (mask & rvBranchBit(leg, branch)) links.push([`ac-bus-${leg}`, `${prefix}-${leg}`]);
      links.push(["ac-bus-n", `${prefix}-n`], ["ac-bus-pe", `${prefix}-pe`]);
    }
  }
  return links;
}

/** Generic declared planning configuration, not a Parallax 5365 replica.
 * Parallax's 5300 range establishes the requested 50 A 120/240 V service
 * and separate 15-position DC board; its converter is deliberately omitted.
 * https://parallaxpower.com/product/5365-power-center/
 * The 20 A branch choices below are declared planning assumptions, not claims
 * about installed breakers. No automatic trip, leakage or AC load-flow model. */
export function makeRvPanel(branches = 4): ComponentDefinition {
  if (!Number.isInteger(branches) || branches < 1 || branches > RV_MAX_BRANCHES) throw new RangeError("RV panel branches must be 1–6 per leg");
  return {
    id: RV_PANEL_ID,
    name: "RV Split-phase AC Distribution Panel 120/240V 50A",
    shortName: "RV split-phase panel",
    subtitle: `Two-pole main · ${branches} branches per leg`,
    category: "distribution", acVoltage: 120,
    // Keep at least 24 px between branch terminals without a tall, empty card.
    width: 280, height: Math.max(210, (branches * 3 + 1) * 24),
    terminals: [
      ...(["l1", "l2", "n", "pe"] as const).map((pole, i) => terminal(`ac-in-${pole}`, `SUPPLY ${pole.toUpperCase()}`, 50, "input", "top", .12 + i * .25)),
      ...RV_LEGS.flatMap((leg, legIndex) => Array.from({ length: branches }, (_, index) => {
        const branch = index + 1;
        return ([leg, "n", "pe"] as const).map((pole, poleIndex) => terminal(
          `${rvBranchPrefix(leg, branch)}-${pole}`, `${leg.toUpperCase()} / ${branch} ${pole === leg ? "L" : pole.toUpperCase()}`,
          20, pole === leg ? "output" : "bidi", legIndex === 0 ? "left" : "right",
          (index * 3 + poleIndex + 1) / (branches * 3 + 1),
        ));
      }).flat()),
      ...(["l1", "l2", "n", "pe"] as const).map((pole, i) => terminal(`ac-bus-${pole}`, `${pole.toUpperCase()} BUS`, 50, "bidi", "bottom", .12 + i * .25)),
    ],
    simulateStates: {
      default: "all-on",
      options: [
        { id: "all-on", label: "All on", intraLinks: contactLinks(branches, allOnMask(branches)) },
        { id: "main-off", label: "Main off", intraLinks: contactLinks(branches, allOnMask(branches) & ~1) },
        { id: "branches-off", label: "Branches off", intraLinks: contactLinks(branches, 1) },
      ],
    },
  };
}

const references = Array.from({ length: RV_MAX_BRANCHES }, (_, i) => makeRvPanel(i + 1));

/** 5300 Series Installation Guidelines Rev B (9/2016), p6 §§2,5–8:
 * 120/240 V, 50 A maximum TWO-POLE main, up to twelve 120 V branches,
 * isolated neutral and separate PE. Breakers are not supplied with the unit.
 * https://m.media-amazon.com/images/I/91GGqcd3dBL.pdf
 * https://parallaxpower.com/product/5355-power-center/
 * Six 20 A branches are David's declared configuration, not factory contents.
 * Model only the AC section: no converter, DC board, GFCI claim or trip model. */
const parallax5355Ac: ComponentDefinition = {
  ...makeRvPanel(3),
  id: PARALLAX_5355_AC_ID,
  name: "Parallax 5355 AC Section · 6 × 20A Configuration",
  shortName: "5355 AC · 6 × 20A",
  subtitle: "50A two-pole main · converter omitted",
  manufacturer: "Parallax Power Supply",
  model: "5355 AC section (6 × 20A configuration)",
};
const electricalShape = (def: ComponentDefinition) => JSON.stringify({
  category: def.category, acVoltage: def.acVoltage, voltage: def.voltage,
  manufacturer: def.manufacturer, model: def.model, renderVariant: def.renderVariant,
  ampDraw: def.ampDraw, ampOutput: def.ampOutput, isFuseBlock: def.isFuseBlock,
  internalDcConnections: def.internalDcConnections, oneWayDcConnections: def.oneWayDcConnections,
  terminals: def.terminals.map(({ id, maxAmp, polarity, direction }) => ({ id, maxAmp, polarity, direction })),
  simulateStates: def.simulateStates,
});
const referenceShapes = references.map(electricalShape);
const parallax5355AcShape = electricalShape(parallax5355Ac);
const branchCountCache = new WeakMap<ComponentDefinition, number | undefined>();

/** Reject a copied ID with changed wiring or ratings before applying controls
 * or protection semantics. Cosmetic customizations retain the profile. */
export function rvPanelBranchCount(def: ComponentDefinition): number | undefined {
  if (def.id === PARALLAX_5355_AC_ID) return electricalShape(def) === parallax5355AcShape ? 3 : undefined;
  if (def.id !== RV_PANEL_ID) return undefined;
  if (branchCountCache.has(def)) return branchCountCache.get(def);
  const index = referenceShapes.indexOf(electricalShape(def));
  const count = index < 0 ? undefined : index + 1;
  branchCountCache.set(def, count);
  return count;
}

export function rvPanelStateOption(def: ComponentDefinition, state?: string): SimulateStateOption | undefined {
  const count = rvPanelBranchCount(def);
  if (count == null) return undefined;
  return { id: state ?? "all-on", label: "Manual breaker positions", intraLinks: contactLinks(count, rvPanelMask(count, state)) };
}

export function rvPanelProtectionProfiles(def: ComponentDefinition): { from: string; to: string; ratingA: number }[] {
  const count = rvPanelBranchCount(def);
  if (count == null) return [];
  return RV_LEGS.flatMap(leg => [
    { from: `ac-in-${leg}`, to: `ac-bus-${leg}`, ratingA: 50 },
    ...Array.from({ length: count }, (_, i) => ({ from: `ac-bus-${leg}`, to: `${rvBranchPrefix(leg, i + 1)}-${leg}`, ratingA: 20 })),
  ]);
}

export const RV_DISTRIBUTION: ComponentDefinition[] = [references[3], parallax5355Ac, {
  id: RV_DC_BOARD_ID,
  name: "RV DC Fuse Board · 15 Circuits",
  shortName: "RV DC fuse board",
  subtitle: "Separate positive fuse slots and negative returns",
  category: "distribution", isFuseBlock: true, fuseSlotType: "ATC",
  equipmentLimitExemption: "fuse", compatibleVoltages: [12],
  width: 220, height: 384,
  // No exact hardware selected: connector ratings remain unknown (0), not
  // inferred from a converter's 65 A output or a fuse's printed number.
  terminals: [
    { id: "in-pos", label: "FEED +", maxAmp: 0, polarity: "positive", direction: "input", side: "top", offset: .25 },
    { id: "in-neg", label: "RETURN −", maxAmp: 0, polarity: "negative", direction: "bidi", side: "top", offset: .75 },
    ...Array.from({ length: 15 }, (_, i): Terminal => ({ id: `c${i + 1}`, label: `FUSE ${i + 1}`, maxAmp: 0, polarity: "positive", direction: "output", side: "left", offset: (i + 1) / 16 })),
    ...Array.from({ length: 15 }, (_, i): Terminal => ({ id: `n${i + 1}`, label: `− ${i + 1}`, maxAmp: 0, polarity: "negative", direction: "bidi", side: "right", offset: (i + 1) / 16 })),
  ],
}];
